import type { ReactElement } from "react";
import type { Variants } from "framer-motion";
import { useKeyboardNavigation } from "../../shared/contexts/KeyboardNavigationContext";
import { useRailNavigation } from "../../shared/hooks";

interface MapRevealSectionProps {
  /** Main heading text */
  readonly title: string;
  /** Subtitle/description text */
  readonly subtitle: string;
  /** Call-to-action button text */
  readonly buttonText: string;
  /** URL to navigate to when button is clicked */
  readonly buttonLink: string;
  /** Navigation function with transition support */
  readonly transitionTo: (path: string, variant?: Variants) => void;
  /** Callback when hover state changes */
  readonly onHoverChange?: (isHovered: boolean) => void;
}

/**
 * Interactive section that reveals the map background on hover
 *
 * Used to highlight map-based browsing functionality with a
 * cinematic reveal effect. The vignette fades when hovering to
 * let the satellite map shine through.
 *
 * Supports keyboard navigation with Enter/Space to activate.
 *
 * @example
 * ```tsx
 * <MapRevealSection
 *   title="Lumi brings world cinema to light"
 *   subtitle="Explore with our interactive map"
 *   buttonText="Explore Map"
 *   buttonLink="/home/browse"
 *   onHoverChange={setIsMapRevealed}
 *   transitionTo={transitionTo}
 *   isFocused={focusedIndex === 0}
 * />
 * ```
 */
export function MapRevealSection({
  title,
  subtitle,
  buttonText,
  buttonLink,
  transitionTo,
  onHoverChange,
}: MapRevealSectionProps): ReactElement {
  const { isNavigationMode } = useKeyboardNavigation();

  const handleClick = () => {
    transitionTo(buttonLink);
  };

  const handleMouseEnter = () => {
    // Only trigger vignette fade on hover if NOT in navigation mode
    if (!isNavigationMode) {
      onHoverChange?.(true);
    }
  };

  const handleMouseLeave = () => {
    // Only restore vignette on mouse leave if NOT in navigation mode
    if (!isNavigationMode) {
      onHoverChange?.(false);
    }
  };

  // Use rail navigation hook with single item for up/down navigation between rails
  const { railRef, handleRailFocus, handleRailBlur, handleRailKeyDown, getCardProps } = useRailNavigation({
    itemCount: 1, // Single-item rail
    onAction: () => {
      // Enter/Space activates the section
      handleClick();
    },
  });

  // Get card props for the single item (index 0)
  const cardProps = getCardProps(0);

  // Wrap focus handlers to trigger vignette fade
  const handleFocus = () => {
    handleRailFocus();
    onHoverChange?.(true); // Fade vignette on focus
  };

  const handleBlur = () => {
    handleRailBlur();
    onHoverChange?.(false); // Restore vignette on blur
  };

  // Prevent rail from receiving focus on click - only allow keyboard focus
  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
  };

  // Rail container pattern matching CollectionRail
  // Uses useRailNavigation for consistent up/down navigation between rails
  // Roving tabindex pattern: ul[tabIndex={0}] enables keyboard focus on the rail container.
  // This is intentional and WCAG-compliant for carousel/rail navigation.
  // See: https://www.w3.org/WAI/ARIA/apg/patterns/carousel/
  return (
    <section className="space-y-4" aria-label="Explore Map">
      {}
      <ul //NOSONAR
        ref={railRef as React.RefObject<HTMLUListElement>}
        data-rail
        tabIndex={0} //NOSONAR
        aria-label="Explore interactive map - Press Enter or Space to navigate"
        className="relative focus:outline-none"
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleRailKeyDown}
        onPointerDown={handlePointerDown}
      >
        <li data-rail-item ref={cardProps.ref}>
          <div //NOSONAR
            tabIndex={-1}
            className={`
                            group relative h-[400px] 
                            flex items-center justify-center overflow-hidden 
                            mx-8 my-4 rounded-lg 
                            border-2 outline outline-transparent border-white/10 
                            delay-200 transition-all duration-1000 
                            hover:border-amber-400/40 
                            cursor-pointer w-[calc(100%-4rem)]
                            ${cardProps.className ? "border-amber-400/40" : ""}
                        `}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClick();
              }
            }}
            role="button"
            aria-label="Explore interactive map"
          >
            <MapRevealContent title={title} subtitle={subtitle} buttonText={buttonText} />
          </div>
        </li>
      </ul>
    </section>
  );
}

interface MapRevealContentProps {
  readonly title: string;
  readonly subtitle: string;
  readonly buttonText: string;
}

function MapRevealContent({ title, subtitle, buttonText }: MapRevealContentProps): ReactElement {
  return (
    <div className="text-center space-y-2 z-10 relative max-w-3xl px-8">
      <h2
        className="
                    font-extralight normal-case text-base sm:text-xl md:text-2xl text-white 
                    leading-tight tracking-normal 
                    transition-all delay-200 duration-1000 
                    group-hover:tracking-wide 
                    group-[.rail-focused]:tracking-wide
                "
      >
        {title}
      </h2>

      <p
        className="
                    font-thin text-sm sm:text-base md:text-lg text-neutral-300 
                    tracking-normal pb-6
                    transition-all delay-200 duration-1000 
                    group-hover:tracking-wide
                    group-[.rail-focused]:tracking-wide
                "
      >
        {subtitle}
      </p>

      <div className="transition-transform delay-200 duration-1000 group-hover:scale-105 group-[.rail-focused]:scale-105 scale-100">
        <div className="inline-block !bg-amber-400 hover:!bg-amber-500 !text-black font-semibold text-lg px-8 py-4 rounded-lg transition-all shadow-lg hover:shadow-xl group-[.rail-focused]:shadow-xl">
          {buttonText}
        </div>
      </div>

      <p
        className="
                    text-sm text-neutral-500 mt-4 transition-opacity duration-1000 
                    group-hover:opacity-100 opacity-60
                    group-[.rail-focused]:opacity-100
                "
      ></p>
    </div>
  );
}
